import React from 'react';
import { Box } from '@mui/material';
import analytics from '../../../utils/analytics';
import PlrModal from '../v3-redesign/landing/PlrModal';
import PlrOptions, { PlrOption } from '../v3-redesign/landing/PlrOptions';
import { PlrButton, PlrNotice } from '../v3-redesign/landing/ui';
import { plr } from '../v3-redesign/landing/tokens';
import type { WorkerType } from '.';

const workerTypeTrackLabel: Record<WorkerType, string> = {
  DEPENDENT_PUBLIC: 'dependiente publico',
  DEPENDENT_PRIVATE: 'dependiente privado',
  INDEPENDENT: 'independiente',
  UNEMPLOYED: 'desempleado',
};

const WORKER_OPTIONS: PlrOption<WorkerType>[] = [
  {
    value: 'DEPENDENT_PRIVATE',
    title: 'Trabajo en una empresa privada',
    description: 'Tengo contrato vigente y recibo sueldo.',
  },
  {
    value: 'DEPENDENT_PUBLIC',
    title: 'Trabajo en el sector público',
    description: 'Municipalidad, ministerio, hospital, Carabineros…',
  },
  {
    value: 'INDEPENDENT',
    title: 'Soy independiente',
    description: 'Mi ingreso principal son boletas de honorarios.',
  },
  {
    value: 'UNEMPLOYED',
    title: 'No tengo empleo formal',
    description: 'Estoy sin trabajo o no recibo sueldo formalmente.',
  },
];

// Errores tras los que la persona puede cerrar la hoja (y volver a ingresar su ClaveÚnica).
const CLOSABLE_ERRORS = [400, 422, 423, 509];

const errorMessage = (code: number) => {
  switch (code) {
    case 400:
    case 422:
      return 'Parece que falta información de tus boletas de honorarios. Cierra esta ventana e ingresa tu ClaveÚnica de nuevo. Si el problema sigue, contáctanos.';
    case 423:
      return 'Parece que no tienes boletas de honorarios registradas. Cierra esta ventana, ingresa tu ClaveÚnica de nuevo y vuelve a elegir tu situación laboral. Si el problema sigue, contáctanos.';
    case 509:
      return 'No pudimos procesar tu evaluación porque uno de nuestros proveedores de información está temporalmente fuera de servicio. Vuelve a intentarlo más tarde.';
    default:
      return 'Ocurrió un error. Por favor contáctanos.';
  }
};

type DependentStatusSheetProps = {
  action: (workerType: WorkerType) => Promise<void>,
  isTriggered: boolean,
};

// 2g (rediseño): cuando el backend pide la situación laboral tras la ClaveÚnica, se pregunta en
// una hoja con las 4 opciones a la vista (antes: diálogo con un Select). No se puede cerrar
// sin elegir, salvo después de un error que pide volver a ingresar la ClaveÚnica.
export default function DependentStatusSheet({ action, isTriggered }: DependentStatusSheetProps) {
  const [open, setOpen] = React.useState(false);
  const [workerType, setWorkerType] = React.useState<WorkerType | ''>('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(0);

  React.useEffect(() => {
    if (isTriggered) setOpen(true);
  }, [isTriggered]);

  const handleSelect = (value: WorkerType) => {
    if (value !== workerType) {
      analytics.track('Click Dropdown', {
        initiator: 'usuario',
        workerType: workerTypeTrackLabel[value],
      });
    }
    // Tras un error que pide volver a ingresar la ClaveÚnica no se puede reintentar desde aquí.
    if (!CLOSABLE_ERRORS.includes(error)) setError(0);
    setWorkerType(value);
  };

  const handleClose = () => {
    analytics.track('Cerrar Dialog', { initiator: 'usuario' });
    setOpen(false);
    setError(0);
    setWorkerType('');
  };

  const handleContinue = async () => {
    if (!workerType) return;
    setError(0);
    setLoading(true);
    try {
      await action(workerType);
    } catch (e) {
      setError(typeof e === 'number' ? e : -1);
    }
    setLoading(false);
  };

  const closable = !!error && CLOSABLE_ERRORS.includes(error);

  return (
    <PlrModal
      open={open}
      onClose={handleClose}
      title="¿Cuál es tu situación laboral?"
      titleId="preloan-worker-type-title"
      testId="preloan-worker-type"
      width={480}
      dismissible={closable}
    >
      <Box
        component="p"
        sx={{
          m: '-6px 0 2px', fontSize: { xs: 14, sm: 15 }, lineHeight: 1.45, color: plr.body,
        }}
      >
        La necesitamos para terminar de calcular tu resultado.
      </Box>
      <PlrOptions
        options={WORKER_OPTIONS}
        value={workerType}
        onChange={handleSelect}
        labelledBy="preloan-worker-type-title"
        testIdPrefix="worker-type"
        disabled={loading}
      />
      {!!error && <PlrNotice tone="error" testId="worker-type-error">{errorMessage(error)}</PlrNotice>}
      <PlrButton
        testId="worker-type-submit"
        onClick={handleContinue}
        disabled={!workerType || !!error}
        loading={loading}
        sx={{ mt: '4px', width: '100%' }}
      >
        Continuar
      </PlrButton>
    </PlrModal>
  );
}
